









































































































































































































































































































































import Vue, { PropType } from 'vue';

import ErrorAlert from '@/components/common/ErrorAlert.vue';
import TimeOffTemplatePicker from '@/components/time-off-calendar/TimeOffTemplatePicker.vue';
import DatePicker from '../common/DatePicker.vue';

import { ErrorManager } from '@/models/error';
import {
  EmployeeTimeOff,
  EmployeeTimeOffEntitlement,
  EmployeeTimeOffFieldError,
  IEmployee,
  IEmployeeTimeOffEntitlementData,
  ITimeOffPolicy,
  ITimeOffPolicyData,
  ITimeOffTypeData,
  ITimeOffUnitData,
  TimeOffDurationType,
  TimeOffPolicy,
  TimeOffType,
  TimeOffUnitEnum,
  IEmployeeTimeOff
} from '@/models';

import { mapActions, mapGetters } from 'vuex';
import { omit } from 'lodash';
import TimeOffHolidayCalendarPicker from '../time-off-calendar/TimeOffHolidayCalendarPicker.vue';
import moment from 'moment';

// mixins
import AuthMixin from '@/mixins/auth';
import { factory as Proxyable } from '@/mixins/proxyable';
import mixins from '@/utils/mixins';

const baseMixins = mixins(AuthMixin, Proxyable<'value', IEmployeeTimeOff>());

export default baseMixins.extend({
  components: {
    ErrorAlert,
    TimeOffTemplatePicker,
    DatePicker,
    TimeOffHolidayCalendarPicker
  },

  props: {
    externalEmployee: {
      type: Object as PropType<IEmployee>
    },

    employeeTimeOffEntitlements: {
      type: Object as PropType<
        Record<number, IEmployeeTimeOffEntitlementData[]>
      >
    },

    fieldError: {
      type: Object,
      default: () => new EmployeeTimeOffFieldError()
    },

    entitlementYearsModified: {
      type: Array as PropType<number[]>
    }
  },

  data() {
    return {
      TimeOffDurationType,
      timeOffPolicyLoading: false,

      selectedYear: new Date().getFullYear(),

      loading: false,
      entitlementLoading: false,

      timeOffPolicyList: [] as ITimeOffPolicy[],

      timeOffUnitOptionList: [
        {
          value: TimeOffUnitEnum.Days,
          display_unit: TimeOffUnitEnum[TimeOffUnitEnum.Days]
        },
        {
          value: TimeOffUnitEnum.Hours,
          display_unit: TimeOffUnitEnum[TimeOffUnitEnum.Hours]
        }
      ] as ITimeOffUnitData[],

      requiredRules: [(v: string) => !!v || 'This field is required']
    };
  },

  computed: {
    ...mapGetters({
      getEmployeeTimeOff: 'employeeTimeOff/getEmployeeTimeOff',
      getEmployeeTimeOffEntitlements:
        'employeeTimeOff/getEmployeeTimeOffEntitlements',
      getTimeOffTemplateById: 'timeOff/getTimeOffTemplateById',
      timeOffPolicies: 'timeOff/getTimeOffPolicies',
      getTimeOffPolicyByTimeOffTypeId: 'timeOff/getTimeOffPolicyByTimeOffTypeId'
    }),

    employeeEntitlements: {
      get(): Record<number, IEmployeeTimeOffEntitlementData[]> {
        return this.employeeTimeOffEntitlements;
      },
      set(value: Record<number, IEmployeeTimeOffEntitlementData[]>) {
        this.$emit('update:employeeTimeOffEntitlements', value);
      }
    },

    yearsModified: {
      get(): number[] {
        return this.entitlementYearsModified;
      },
      set(value: number[]) {
        this.$emit('update:entitlementYearsModified', value);
      }
    },

    expiryDateRule() {
      return (effectiveDate: string) => (v: string) => {
        if (effectiveDate) {
          return (
            new Date(effectiveDate) < new Date(v) ||
            'Expiry date should be after effective date'
          );
        }
        return !!v || 'This field is required';
      };
    },

    getEmployeeEntitlementsOfSelectedYear(): IEmployeeTimeOffEntitlementData[] {
      return this.employeeEntitlements[this.selectedYear];
    }
  },

  methods: {
    ...mapActions({
      updateEmployeeTimeOff: 'employeeTimeOff/updateEmployeeTimeOff',
      fetchTimeOffTemplate: 'timeOff/fetchTimeOffTemplate',
      fetchTimeOffPolicies: 'timeOff/fetchTimeOffPolicies',
      createMultipleEmployeeTimeOffEntitlements:
        'employeeTimeOff/createMultipleEmployeeTimeOffEntitlements'
    }),

    async genEmployeeEntitlements(templateId: number) {
      if (!templateId) {
        this.employeeEntitlements[this.selectedYear] = [];
        return;
      }

      if (!this.getTimeOffTemplateById(templateId)) {
        await this.fetchTimeOffTemplate(templateId).catch(error => {
          this.$emit(
            'update:errorMessage',
            ErrorManager.extractApiError(error)
          );
        });
      }

      const templateTimeOffTypes: TimeOffType[] = this.getTimeOffTemplateById(
        templateId
      ).timeoffs;

      const entitlements = templateTimeOffTypes.map(data => {
        const policy: TimeOffPolicy = this.getTimeOffPolicyByTimeOffTypeId(
          data.id
        );

        const effectiveDate =
          policy.timeoff_duration_type == TimeOffDurationType.Static
            ? moment(policy.effective_date)
                .set('year', this.selectedYear)
                .format('YYYY-MM-DD')
            : null;

        const expiryDate =
          policy.timeoff_duration_type == TimeOffDurationType.Static
            ? moment(effectiveDate)
                .add(1, 'year')
                .subtract(1, 'days')
                .format('YYYY-MM-DD')
            : null;

        return new EmployeeTimeOffEntitlement({
          ...policy,
          id: undefined,
          timeoff: data!,
          effective_date: effectiveDate,
          expiry_date: expiryDate
        });
      });

      //we use this to keep object reactivity. Vue cannot detect object property addition
      Vue.set(this.employeeEntitlements, this.selectedYear, entitlements);

      this.updateYearsModifiedList();
    },

    async tryGetOrFetchTimeOffPolicies(): Promise<ITimeOffPolicy[]> {
      if (!this.timeOffPolicies.length) {
        this.timeOffPolicyLoading = true;
        await this.fetchTimeOffPolicies()
          .catch(error => {
            this.$emit(
              'update:errorMessage',
              ErrorManager.extractApiError(error)
            );
          })
          .finally(() => {
            this.timeOffPolicyLoading = false;
          });
      }
      return this.timeOffPolicies;
    },

    updateYearsModifiedList() {
      if (!this.yearsModified.includes(this.selectedYear)) {
        this.yearsModified.push(this.selectedYear);
      }
    },

    async genTimeOffPolicyList() {
      const timeOffPolicies = await this.tryGetOrFetchTimeOffPolicies();

      this.timeOffPolicyList = timeOffPolicies.filter(
        policy =>
          !this.getEmployeeEntitlementsOfSelectedYear?.some(
            entitlement =>
              entitlement.timeoff &&
              (entitlement.timeoff as ITimeOffTypeData).id == policy.id
          )
      );
    },

    updateEntitlementAmountValidity(
      is_carryover_supported: boolean,
      entitlement: ITimeOffPolicy
    ) {
      entitlement.entitlement_amount_validity = is_carryover_supported
        ? entitlement.entitlement_amount_validity
        : 0;

      this.updateYearsModifiedList();
    },

    updateEntitlementPolicy(item: ITimeOffPolicy, index: number) {
      const effectiveDate =
        item.timeoff_duration_type == TimeOffDurationType.Static
          ? moment(item.effective_date)
              .set('year', this.selectedYear)
              .format('YYYY-MM-DD')
          : null;

      const expiryDate =
        item.timeoff_duration_type == TimeOffDurationType.Static
          ? moment(effectiveDate)
              .add(1, 'year')
              .subtract(1, 'days')
              .format('YYYY-MM-DD')
          : null;

      const entitlementData = new EmployeeTimeOffEntitlement({
        ...item,
        id: undefined,
        effective_date: effectiveDate,
        expiry_date: expiryDate
      });
      this.employeeEntitlements[this.selectedYear]?.splice(
        index,
        1,
        entitlementData
      );

      this.updateYearsModifiedList();
    },

    async addEntitlementPolicy() {
      await this.genTimeOffPolicyList();
      const entitlement = {} as IEmployeeTimeOffEntitlementData;

      this.employeeEntitlements[this.selectedYear]?.unshift(entitlement);
      this.updateYearsModifiedList();
    },

    removeEntitlementPolicy(index: number) {
      this.employeeEntitlements[this.selectedYear]?.splice(index, 1);
      this.updateYearsModifiedList();
      this.genTimeOffPolicyList();
    },

    searchEmployeeTimeOffEntitlements(year: number) {
      if (!this.employeeEntitlements[year]) {
        this.$emit('getEmployeeTimeOffEntitlements', year);
      }
    },

    clearAllErrors(): void {
      this.$emit('clearAllErrors');
    },

    async saveEmployeeTimeOff(): Promise<void> {
      this.clearAllErrors();

      const timeOffPayload = new EmployeeTimeOff(
        omit(this.internalValue, ['entitlements'])
      );
      await this.updateEmployeeTimeOff(timeOffPayload);

      for (const year of this.yearsModified) {
        const entitlements = this.employeeEntitlements[year]?.map(data => {
          return new EmployeeTimeOffEntitlement({
            ...data,
            timeoff: (data.timeoff as ITimeOffPolicyData).id
          });
        });

        await this.createMultipleEmployeeTimeOffEntitlements({
          employee: timeOffPayload.employee,
          year: year,
          entitlements
        });
      }
    }
  },

  mounted() {
    this.tryGetOrFetchTimeOffPolicies();
  }
});
